/**
 * Unified thinking-copy registry — the single source for both surfaces that
 * narrate a wait:
 *
 *   - `header`:  terse status text rendered in the chat header (mode strategies).
 *   - `phrases`: first-person cycling text rendered beside the thinking
 *     indicator's logo mark in its waiting phase (`ThinkingIndicator`).
 *
 * The two surfaces deliberately speak in different registers (header = status,
 * indicator = the assistant's voice), which is why a group carries both fields
 * rather than one string.
 *
 * Both fields are optional:
 *   - header-only groups are migrated mode-strategy copy (Discovery stage text,
 *     Advice phase labels) with no indicator presentation;
 *   - `generic` is phrases-only — the indicator's default pool.
 *
 * Phrase pools may supply only `stage1`/`stage2`: `ThinkingIndicator.setPhrases`
 * merges over the generic pool, so stage3/stage4 overflow escalation is
 * inherited unless a group authors its own. Author stage3/stage4 only for
 * waits measured to exceed ~10s (the sub-agent tools).
 *
 * Group keys for tool phases match the backend `advisingPhase` values emitted
 * by SonnetGenerator / toolStateHeartbeat verbatim — they arrive over SSE, so
 * lookups MUST go through `getThinkingGroup` (Object.hasOwn) rather than bare
 * indexing, or a hostile/buggy phase like `"constructor"` resolves to a
 * prototype member.
 */

const GROUPS = {
  // ===== Generic fallback — the indicator's default cycling pool =====
  generic: {
    phrases: {
      // Stage 1: initial processing
      stage1: [
        'Thinking',
        'Processing',
        'Considering',
        'Reflecting',
        'Analyzing',
        'Pondering',
        'Contemplating',
        'Evaluating',
        'Noodling'
      ],
      // Stage 2: deeper analysis. Every phrase here renders as a present-tense
      // progress claim — nothing that implies a retrieval on turns without one
      // (the `retrieving` group owns memory-search copy).
      stage2: [
        'Examining patterns',
        'Connecting the dots',
        'Finding connections',
        'Understanding context',
        'Exploring possibilities'
      ],
      // Stage 3: formulating response
      stage3: [
        'Formulating thoughts',
        'Composing response',
        'Gathering my thoughts',
        'Putting it together',
        'Preparing response',
        'Generating insights'
      ],
      // Stage 4: extended wait (overflow)
      stage4: [
        'Still thinking',
        'Almost there',
        'Taking a bit longer',
        'Working on this',
        'Bear with me'
      ]
    }
  },

  // ===== Tool phases (backend advisingPhase values) =====

  // The retrieval itself is ~7ms (a cached-digest lookup); this copy displays
  // across the model's time-to-first-token afterwards. Keep it in the
  // past-completed register — a retrieval genuinely just happened — and do not
  // author stage3/stage4: this wait never approaches the overflow threshold.
  retrieving: {
    header: 'Searching memory',
    phrases: {
      stage1: [
        "Looking through what you've told me",
        'Checking what I know',
        'Looking back through our conversations'
      ],
      stage2: [
        'Pulling up the relevant history',
        'Connecting this to what I remember'
      ]
    }
  },

  // Sub-agent tool: measured 14.6s executor. Full stage3/stage4 — 15s+ is
  // normal for this wait, not exceptional.
  choosing_practice: {
    header: 'Choosing an experiment',
    phrases: {
      stage1: [
        'Thinking about what would help',
        'Considering what might fit here'
      ],
      stage2: [
        'Weighing a few options',
        'Comparing a couple of approaches'
      ],
      stage3: [
        'Settling on the right one',
        'Picking the one that fits best'
      ],
      stage4: [
        'Still choosing — bear with me',
        'Taking a little longer than usual'
      ]
    }
  },

  // Stage2 must stand alone: if practice selection takes >5s before this phase
  // starts, the preserved stage clock enters here at stage2 and stage1 never
  // renders. Do not "fix" that by resetting the clock — it would re-break
  // heartbeat escalation.
  designing_practice: {
    header: 'Designing an experiment',
    phrases: {
      stage1: [
        'Shaping something you can try',
        'Sketching out an exercise'
      ],
      stage2: [
        'Working out the steps',
        'Building this out for you'
      ],
      stage3: [
        'Getting the details right',
        'Refining the steps'
      ],
      stage4: [
        'Putting on the finishing touches',
        'Almost done building this'
      ]
    }
  },

  // Sub-agent tool: measured 7-12s executor.
  composing_memo: {
    header: 'Writing a note',
    phrases: {
      stage1: [
        'Choosing my words',
        'Thinking about how to say this'
      ],
      stage2: [
        'Getting the tone right',
        'Writing this with care'
      ],
      stage3: [
        'Reading it back over',
        'Tightening up the wording'
      ],
      stage4: [
        'Giving it one more pass',
        'Nearly finished writing'
      ]
    }
  },

  // The wait is the model writing the takeaway as tool input (~1-3s) — the
  // executor is instant. stage1/stage2 only; it never approaches overflow.
  composing_takeaway: {
    header: 'Pulling this together',
    phrases: {
      stage1: [
        'Gathering the threads',
        'Collecting what we covered'
      ],
      stage2: [
        'Working out what matters most',
        'Distilling this down'
      ]
    }
  },

  // ===== Header-only groups (migrated mode-strategy copy) =====

  // Advice phase labels
  entry: { header: 'Starting conversation' },
  scoping: { header: 'Gathering information' },

  // Discovery stage text — arrays preserve the existing random-pick behaviour
  // byte-for-byte; the pick happens at the render site via pickHeader().
  loading: { header: ['Loading Discovery'] },
  intermediary: {
    header: [
      'Reflecting on what you shared',
      'Taking a moment to consider',
      'Thinking about what you said',
      'Processing your responses',
      'Making sense of what you shared'
    ]
  },
  l2_thinking: {
    header: [
      'Considering this carefully',
      'Thinking about what you shared',
      'Reflecting on what you said',
      'Gathering my thoughts',
      'Taking a moment to understand',
      'Analyzing what you shared',
      'Thinking carefully about this'
    ]
  },
  l2_starting: {
    header: [
      'Setting up our conversation',
      'Getting things ready',
      'Preparing our discussion',
      'Gathering my thoughts'
    ]
  },
  next_assessment: {
    header: [
      'Getting the next assessment ready',
      'Preparing the next step',
      'Moving things along',
      "Queuing up what's next"
    ]
  },
  completing: {
    header: [
      'Wrapping up this path',
      'Finishing things up'
    ]
  }
};

/**
 * Safe registry lookup. The only supported way to resolve a group key —
 * guards against prototype-member resolution for server-supplied keys.
 *
 * @param {string|null|undefined} key - Group key (e.g. an advisingPhase value)
 * @returns {Object|undefined} The group, or undefined for unknown/missing keys
 */
export function getThinkingGroup(key) {
  if (typeof key !== 'string') return undefined;
  return Object.hasOwn(GROUPS, key) ? GROUPS[key] : undefined;
}

/**
 * Resolve a group's header text. The only supported way to read `header` —
 * centralising the string-vs-array handling so an array can never silently
 * render as `"a,b,c"`. Arrays re-randomise on every call, preserving
 * Discovery's per-render variant pick.
 *
 * @param {Object|null|undefined} group - A group object from getThinkingGroup()
 * @returns {string|null} Header text, or null when the group has none
 */
export function pickHeader(group) {
  const header = group?.header;
  if (Array.isArray(header)) {
    return header[Math.floor(Math.random() * header.length)] ?? null;
  }
  return typeof header === 'string' ? header : null;
}

export const thinkingGroups = GROUPS;
